import React, { RefObject } from "react";

import { CycleGroupBox } from "./CycleGroupBox";
import { EdgeRenderer } from "./EdgeRenderer";
import { EmployeeCard } from "./EmployeeCard";

import type { CycleGroup, OrgEmployee } from "../types";

import css from "../OrgChart.module.scss";
import type { LayoutNode } from "../layout";

type OrgEdge = { source: string; target: string };

interface NodeInfo {
	childCount: number;
	isExpanded: boolean;
}

interface ChartCanvasProps {
	contentRef: RefObject<HTMLDivElement | null>;
	contentWidth: number;
	contentHeight: number;
	visibleCycleGroups: CycleGroup[];
	visibleEmployees: OrgEmployee[];
	visibleEdges: OrgEdge[];
	positions: Map<string, LayoutNode>;
	nodeInfo: Map<string, NodeInfo>;
	offsetX: number;
	offsetY: number;
	highlightedNodeId: string | null;
	selectedNodeId: string | null;
	onNodeClick: (employeeId: string) => void;
	onNodeToggle: (employeeId: string) => void;
}

export function ChartCanvas({
	contentRef,
	contentWidth,
	contentHeight,
	visibleCycleGroups,
	visibleEmployees,
	visibleEdges,
	positions,
	nodeInfo,
	offsetX,
	offsetY,
	highlightedNodeId,
	selectedNodeId,
	onNodeClick,
	onNodeToggle,
}: ChartCanvasProps) {
	return (
		<div ref={contentRef} className={css.content} style={{ width: contentWidth, height: contentHeight }}>
			{visibleCycleGroups.map((group) => (
				<CycleGroupBox key={group.id} group={group} positions={positions} offsetX={offsetX} offsetY={offsetY} />
			))}

			<EdgeRenderer
				edges={visibleEdges}
				positions={positions}
				offsetX={offsetX}
				offsetY={offsetY}
				contentWidth={contentWidth}
				contentHeight={contentHeight}
			/>

			{visibleEmployees.map((employee) => {
				const position = positions.get(employee.id);
				if (!position) return null;
				const info = nodeInfo.get(employee.id);
				return (
					<EmployeeCard
						key={employee.id}
						employee={employee}
						style={{
							left: position.x + offsetX,
							top: position.y + offsetY,
						}}
						childCount={info?.childCount ?? 0}
						isExpanded={info?.isExpanded ?? false}
						isHighlighted={highlightedNodeId === employee.id}
						isSelected={selectedNodeId === employee.id}
						onToggle={() => onNodeToggle(employee.id)}
						onClick={() => onNodeClick(employee.id)}
					/>
				);
			})}
		</div>
	);
}
